import { useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { DocumentStatus } from "@/types/documento";
import { cn } from "@/lib/class-control";
import { ChevronDown, X } from "lucide-react";

interface StageFilterProps {
  value: DocumentStatus[];
  onChange: (value: DocumentStatus[]) => void;
  className?: string;
}

const STAGE_LABELS: Record<DocumentStatus, string> = {
  "Produção": "Produção",
  "Pré-Produção": "Pré-Produção",
  "Pós-Produção": "Pós-Produção",
  "Desenvolvimento": "Desenvolvimento",
  "Comercialização": "Comercialização",
};

/**
 * StageFilter Component
 *
 * Multi-select filter for document production stages using Popover + Checkbox pattern.
 * Shows active filter count badge and clear button.
 *
 * @param value - Array of selected document stages
 * @param onChange - Callback when selection changes
 * @param className - Optional additional CSS classes
 */
export function StageFilter({
  value,
  onChange,
  className,
}: StageFilterProps) {
  const [open, setOpen] = useState(false);

  const handleToggle = (stage: DocumentStatus) => {
    const newValue = value.includes(stage)
      ? value.filter((s) => s !== stage)
      : [...value, stage];
    onChange(newValue);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  const activeCount = value.length;
  const hasActiveFilters = activeCount > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "border border-[#5D5D5D] rounded-[10px] px-[20px] py-[10px] h-[50px] min-w-[200px]",
            "bg-transparent text-[#F6F6F6] text-[16px] font-['Plus_Jakarta_Sans']",
            "hover:bg-[#15203C]/50 transition-colors",
            "flex items-center justify-between gap-[10px]",
            className
          )}
        >
          <span className="flex items-center gap-[8px]">
            <span>Etapa</span>
            {hasActiveFilters && (
              <span className="inline-flex items-center justify-center size-[20px] rounded-full bg-[#7846FF] text-white text-[12px] font-bold">
                {activeCount}
              </span>
            )}
          </span>
          <div className="flex items-center gap-[8px]">
            {hasActiveFilters && (
              <button
                onClick={handleClear}
                className="hover:bg-white/10 rounded-full p-[2px] transition-colors"
                aria-label="Limpar filtros"
              >
                <X className="size-[16px]" />
              </button>
            )}
            <ChevronDown
              className={cn(
                "size-[16px] transition-transform",
                open && "rotate-180"
              )}
            />
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[280px] bg-[#15203C] border-[#5D5D5D] p-[16px]"
      >
        <div className="flex flex-col gap-[12px]">
          <div className="flex items-center justify-between mb-[8px]">
            <h3 className="text-[14px] font-semibold text-white font-['Plus_Jakarta_Sans']">
              Etapa
            </h3>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClear}
                className="h-auto p-0 text-[12px] text-[#7846FF] hover:text-[#7846FF]/80 hover:bg-transparent"
              >
                Limpar tudo
              </Button>
            )}
          </div>
          {(Object.keys(STAGE_LABELS) as DocumentStatus[]).map((stage) => (
            <div
              key={stage}
              className="flex items-center gap-[12px] hover:bg-white/5 p-[8px] rounded-[6px] transition-colors cursor-pointer"
              onClick={() => handleToggle(stage)}
            >
              <Checkbox
                id={`stage-${stage}`}
                checked={value.includes(stage)}
                onCheckedChange={() => handleToggle(stage)}
                className="pointer-events-none"
              />
              <label
                htmlFor={`stage-${stage}`}
                className="text-[14px] text-[#F6F6F6] font-['Plus_Jakarta_Sans'] flex-1 cursor-pointer"
              >
                {STAGE_LABELS[stage]}
              </label>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
